import { useEffect, useState } from 'react';

import {
  EVENT_AUTHENTICATE_NAME,
  EVENT_NAME_CLOSE_LOGIN,
  EVENT_NAME_CLOSE_SIGN_UP,
  EVENT_NAME_LOGIN,
  EVENT_NAME_SIGN_UP,
} from '@/utility/session';

// Custom events for mobile menu modal
export const EVENT_MOBILE_MENU_OPEN = 'mobile-menu-open';
export const EVENT_MOBILE_MENU_CLOSE = 'mobile-menu-close';

// Custom events for modal state tracking (separate from authentication events)
export const EVENT_MODAL_STATE_LOGIN_OPEN = 'modal-state-login-open';
export const EVENT_MODAL_STATE_LOGIN_CLOSE = 'modal-state-login-close';
export const EVENT_MODAL_STATE_SIGNUP_OPEN = 'modal-state-signup-open';
export const EVENT_MODAL_STATE_SIGNUP_CLOSE = 'modal-state-signup-close';

/**
 * Custom hook to track when login, signup, or mobile menu modals are open
 * Returns true when any of these modals are open, false when all are closed
 */
export const useAuthModalState = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleAuthEvent = (event: CustomEvent) => {
      const eventName = event.detail?.name;

      if (eventName === EVENT_NAME_LOGIN || eventName === EVENT_NAME_SIGN_UP) {
        setIsAuthModalOpen(true);
      } else if (eventName === EVENT_NAME_CLOSE_LOGIN || eventName === EVENT_NAME_CLOSE_SIGN_UP) {
        setIsAuthModalOpen(false);
      }
    };

    const handleModalStateEvent = (event: Event) => {
      const eventType = event.type;

      if (
        eventType === EVENT_MODAL_STATE_LOGIN_OPEN ||
        eventType === EVENT_MODAL_STATE_SIGNUP_OPEN
      ) {
        setIsAuthModalOpen(true);
      } else if (
        eventType === EVENT_MODAL_STATE_LOGIN_CLOSE ||
        eventType === EVENT_MODAL_STATE_SIGNUP_CLOSE
      ) {
        setIsAuthModalOpen(false);
      }
    };

    const handleMobileMenuOpen = () => {
      setIsMobileMenuOpen(true);
    };

    const handleMobileMenuClose = () => {
      setIsMobileMenuOpen(false);
    };

    // Listen for authentication events (for external control)
    document.addEventListener(EVENT_AUTHENTICATE_NAME, handleAuthEvent as EventListener);

    // Listen for modal state events (for internal state tracking)
    document.addEventListener(EVENT_MODAL_STATE_LOGIN_OPEN, handleModalStateEvent);
    document.addEventListener(EVENT_MODAL_STATE_LOGIN_CLOSE, handleModalStateEvent);
    document.addEventListener(EVENT_MODAL_STATE_SIGNUP_OPEN, handleModalStateEvent);
    document.addEventListener(EVENT_MODAL_STATE_SIGNUP_CLOSE, handleModalStateEvent);

    // Listen for mobile menu events
    document.addEventListener(EVENT_MOBILE_MENU_OPEN, handleMobileMenuOpen);
    document.addEventListener(EVENT_MOBILE_MENU_CLOSE, handleMobileMenuClose);

    return () => {
      document.removeEventListener(EVENT_AUTHENTICATE_NAME, handleAuthEvent as EventListener);
      document.removeEventListener(EVENT_MODAL_STATE_LOGIN_OPEN, handleModalStateEvent);
      document.removeEventListener(EVENT_MODAL_STATE_LOGIN_CLOSE, handleModalStateEvent);
      document.removeEventListener(EVENT_MODAL_STATE_SIGNUP_OPEN, handleModalStateEvent);
      document.removeEventListener(EVENT_MODAL_STATE_SIGNUP_CLOSE, handleModalStateEvent);
      document.removeEventListener(EVENT_MOBILE_MENU_OPEN, handleMobileMenuOpen);
      document.removeEventListener(EVENT_MOBILE_MENU_CLOSE, handleMobileMenuClose);
    };
  }, []);

  const isAnyModalOpen = isAuthModalOpen || isMobileMenuOpen;

  return isAnyModalOpen;
};
